import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { applyNodeChanges, applyEdgeChanges } from '@xyflow/react'
import type { NodeChange, EdgeChange, Node as RFNode, Edge as RFEdge } from '@xyflow/react'

import type { SwatchKey } from '@/views/maps/RoadmapEditorDesignTokens'
import type { RoadmapEdge, MindmapNodeData } from '@/views/maps/types'

// ============================================================
// TYPES
// ============================================================

/** Грязный/удалённый набор как Record<id,true> — НЕ Set (Immer-нюанс A3). */
type IdSet = Record<string, true>

/**
 * Нода mindmap-дерева на клиенте. Generic-нода (Pattern 2): живёт в той же таблице Node
 * с data.kind='mindmap', иерархия — на таблице Edge (parent=source → child=target).
 * type='mindmap' (аддитивное значение enum NodeType из плана 01).
 */
export interface MindmapNode {
  id: string
  type: 'mindmap'
  position: { x: number; y: number }
  width?: number
  height?: number
  data: MindmapNodeData
  /** Runtime-флаг React Flow (applyNodeChanges). */
  selected?: boolean
}

/** Сериализуемый снимок mindmap-дерева для undo/redo (ноды + рёбра, без selection). */
export interface MindmapSnapshot {
  nodes: MindmapNode[]
  edges: RoadmapEdge[]
}

export interface MindmapState {
  nodes: MindmapNode[]
  edges: RoadmapEdge[]
  selectedId: string | null
  dirty: { nodes: IdSet; edges: IdSet }
  deleted: { nodeIds: IdSet; edgeIds: IdSet }
  past: MindmapSnapshot[]
  future: MindmapSnapshot[]
  saveStatus: 'idle' | 'saving' | 'saved' | 'error'
  /** Клиентский режим карты (LOCK-01): view (по умолчанию) или edit (держим лок). */
  mode: 'view' | 'edit'
  /**
   * Снимок активного лока редактирования (из status-poll / heartbeat-ответа).
   * Даты — строки (приходят сериализованными из JSON-роута). null = лок свободен.
   */
  lock: {
    holderId: string
    holderName: string | null
    sessionId: string
    takeoverRequestedById: string | null
    takeoverRequestedAt: string | null
  } | null
}

// ============================================================
// CONSTANTS
// ============================================================

/** Глубина стека undo (MIND-* / RM-07) — кольцо, дроп старейшего при переполнении. */
const UNDO_DEPTH = 100

// ============================================================
// HELPERS
// ============================================================

/**
 * Клиентский id новых элементов. crypto.randomUUID() доступен только в secure
 * context (https или localhost) — по http с LAN/туннель-origin он undefined,
 * поэтому фолбэк на getRandomValues (UUID v4), затем на Math.random. Schema
 * `id String @id` принимает любую строку — сервер делает идемпотентный upsert.
 * Копия roadmap.createId (тот же контракт).
 */
export function createId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined

  if (typeof c?.randomUUID === 'function') return c.randomUUID()

  if (typeof c?.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16))

    b[6] = (b[6] & 0x0f) | 0x40
    b[8] = (b[8] & 0x3f) | 0x80

    const h = Array.from(b, x => x.toString(16).padStart(2, '0'))

    return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`
  }

  return `id-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
}

const initialState: MindmapState = {
  nodes: [],
  edges: [],
  selectedId: null,
  dirty: { nodes: {}, edges: {} },
  deleted: { nodeIds: {}, edgeIds: {} },
  past: [],
  future: [],
  saveStatus: 'idle',
  mode: 'view',
  lock: null
}

/**
 * Сериализуемый снимок структуры/атрибутов (без selection/viewport).
 *
 * WR-05 (предел памяти undo, осознанно принят для v1): каждый снимок — полный deep-clone
 * дерева через JSON.parse(JSON.stringify). Прямые редьюсеры (setColor/updateNodeData/
 * setExpanded/addChild/…) пушат снимок в кольцо past глубиной UNDO_DEPTH (100). Для
 * предельной карты (2000 нод — лимит импорта) это до ~100 копий дерева в памяти вкладки
 * (порядка десятков МБ). Reconcile-путь (правки с канвы через мост) history НЕ пушит, так
 * что основной поток правок Mind-Elixir сюда не попадает. Если профилирование покажет
 * деградацию/OOM на крупных картах — мигрировать на structural sharing или дельта-снимки
 * (хранить патч вместо полного дерева). Для v1 фиксированного кольца в 100 снимков
 * достаточно; рефактор отложен намеренно.
 */
function snapshot(state: MindmapState): MindmapSnapshot {
  return {
    nodes: JSON.parse(JSON.stringify(state.nodes)),
    edges: JSON.parse(JSON.stringify(state.edges))
  }
}

/** Push снимок в past (кольцо глубиной 100) ПЕРЕД мутацией и очистить future. */
function pushHistory(state: MindmapState): void {
  state.past.push(snapshot(state))

  if (state.past.length > UNDO_DEPTH) state.past.shift() // дроп старейшего

  state.future = []
}

/**
 * Согласовать dirty/deleted после восстановления снимка (undo/redo, CR-02).
 *
 * Восстановление среза НЕ помечает элементы dirty, поэтому автосохранение их не видит
 * → данные теряются. Здесь: всё, что есть ПОСЛЕ восстановления, помечаем dirty (upsert),
 * а всё, что было ДО, но исчезло (было добавлено и откатилось), помечаем deleted.
 *
 * @param before — id-наборы по нодам/рёбрам в состоянии ДО восстановления.
 */
function reconcileDirtyAfterRestore(state: MindmapState, before: { nodes: string[]; edges: string[] }): void {
  const afterNodes = new Set(state.nodes.map(n => n.id))
  const afterEdges = new Set(state.edges.map(e => e.id))

  // Всё восстановленное → dirty (upsert при следующем флаше). Снять из deleted, если было.
  for (const n of state.nodes) {
    state.dirty.nodes[n.id] = true
    delete state.deleted.nodeIds[n.id]
  }

  for (const e of state.edges) {
    state.dirty.edges[e.id] = true
    delete state.deleted.edgeIds[e.id]
  }

  // Было до восстановления, но исчезло → deleted (сервер удалит). Снять из dirty.
  for (const id of before.nodes) {
    if (!afterNodes.has(id)) {
      state.deleted.nodeIds[id] = true
      delete state.dirty.nodes[id]
    }
  }

  for (const id of before.edges) {
    if (!afterEdges.has(id)) {
      state.deleted.edgeIds[id] = true
      delete state.dirty.edges[id]
    }
  }
}

/**
 * Согласовать dirty/deleted после ПОЛЬЗОВАТЕЛЬСКОЙ правки канвы (reconcileCanvas, WR-06).
 *
 * В отличие от reconcileDirtyAfterRestore (undo/redo — там неизвестно, что изменилось,
 * поэтому помечаем dirty всё восстановленное), здесь у нас есть и старый, и новый снимок
 * → помечаем dirty ТОЛЬКО реально изменившиеся/новые ноды/рёбра (content-diff). Иначе
 * правка одной ноды раздувала автосейв до размера всей карты и упиралась в MAX_DELTA_BYTES
 * (риск 413 на крупной карте).
 *
 * @param before — снимок нод/рёбер ДО правки (по id, для diff и пометки deleted).
 */
function reconcileDirtyAfterEdit(
  state: MindmapState,
  before: { nodes: Map<string, MindmapNode>; edges: Map<string, RoadmapEdge> }
): void {
  const afterNodes = new Set(state.nodes.map(n => n.id))
  const afterEdges = new Set(state.edges.map(e => e.id))

  // Новые/изменённые ноды → dirty. Сравнение содержимого через JSON (data — единственное
  // значимое поле для mindmap; position/width/height фиктивны, IN-02).
  for (const n of state.nodes) {
    const prev = before.nodes.get(n.id)

    if (!prev || JSON.stringify(prev.data) !== JSON.stringify(n.data)) {
      state.dirty.nodes[n.id] = true
    }

    delete state.deleted.nodeIds[n.id]
  }

  // Новые/изменённые рёбра → dirty (изменение source = reparent).
  for (const e of state.edges) {
    const prev = before.edges.get(e.id)

    if (!prev || prev.source !== e.source || prev.target !== e.target) {
      state.dirty.edges[e.id] = true
    }

    delete state.deleted.edgeIds[e.id]
  }

  // Исчезнувшие → deleted.
  for (const id of before.nodes.keys()) {
    if (!afterNodes.has(id)) {
      state.deleted.nodeIds[id] = true
      delete state.dirty.nodes[id]
    }
  }

  for (const id of before.edges.keys()) {
    if (!afterEdges.has(id)) {
      state.deleted.edgeIds[id] = true
      delete state.dirty.edges[id]
    }
  }
}

/**
 * Заливка содержимого канвы в state: ноды/связи + сброс выделения, dirty/deleted,
 * undo-истории и saveStatus. НЕ трогает mode/lock — это решает вызывающий reducer.
 */
function hydrateContent(state: MindmapState, payload: MindmapSnapshot) {
  state.nodes = payload.nodes
  state.edges = payload.edges
  state.selectedId = null
  state.dirty = { nodes: {}, edges: {} }
  state.deleted = { nodeIds: {}, edgeIds: {} }
  state.past = []
  state.future = []
  state.saveStatus = 'idle'
}

/** id входящего ребра ноды (toNodeId/target === nodeId) и id родителя. */
function incomingEdge(state: MindmapState, nodeId: string): RoadmapEdge | undefined {
  return state.edges.find(e => e.target === nodeId)
}

/** Множество всех транзитивных потомков nodeId (для анти-цикла reparent, T-14-02-01). */
function descendantsOf(state: MindmapState, nodeId: string): Set<string> {
  const childrenOf = new Map<string, string[]>()

  for (const e of state.edges) {
    const list = childrenOf.get(e.source) ?? []

    list.push(e.target)
    childrenOf.set(e.source, list)
  }

  const out = new Set<string>()

  const walk = (id: string) => {
    for (const child of childrenOf.get(id) ?? []) {
      if (out.has(child)) continue
      out.add(child)
      walk(child)
    }
  }

  walk(nodeId)

  return out
}

/** Создать корневую ноду без родителя (пустая карта). Помечает dirty, выделяет новую ноду. */
function attachRoot(state: MindmapState): void {
  const nodeId = createId()
  const data: MindmapNodeData = { kind: 'mindmap', title: '', isRoot: true }

  state.nodes.push({ id: nodeId, type: 'mindmap', position: { x: 0, y: 0 }, data })
  state.dirty.nodes[nodeId] = true

  // Выделить новую ноду — для авто-входа в inline-edit на рендере (D-01/D-02).
  state.selectedId = nodeId
}

/** Создать ноду-потомка parentId (общий путь addChild/addSibling). */
function attachChild(state: MindmapState, parentId: string): void {
  const nodeId = createId()
  const edgeId = createId()
  const data: MindmapNodeData = { kind: 'mindmap', title: '' }

  state.nodes.push({ id: nodeId, type: 'mindmap', position: { x: 0, y: 0 }, data })
  state.edges.push({ id: edgeId, source: parentId, target: nodeId })

  state.dirty.nodes[nodeId] = true
  state.dirty.edges[edgeId] = true

  // Выделить новую ноду — для авто-входа в inline-edit на рендере (D-01/D-02).
  state.selectedId = nodeId
}

// ============================================================
// SLICE
// ============================================================

const slice = createSlice({
  name: 'mindmap',
  initialState,
  reducers: {
    /** Заливка начального снимка канвы (из RSC props / GET). Сбрасывает сессию. */
    hydrate(state, action: PayloadAction<MindmapSnapshot>) {
      hydrateContent(state, action.payload)
      state.mode = 'view'
      state.lock = null
    },

    /** Рефетч содержимого канвы (на «Обновить» / автообновлении), НЕ трогает mode/lock. */
    hydrateCanvas(state, action: PayloadAction<MindmapSnapshot>) {
      hydrateContent(state, action.payload)
    },

    /**
     * Сверка содержимого канвы со снимком ПРАВКИ от движка Mind-Elixir (мост, 14-08).
     *
     * В отличие от hydrateCanvas (read-only заливка рефетча/превью версии — чистит dirty),
     * reconcileCanvas — это ПОЛЬЗОВАТЕЛЬСКАЯ правка с канвы: заменяет ноды/рёбра снимком и
     * помечает диф dirty/deleted (через reconcileDirtyAfterRestore), чтобы автосейв (план 04)
     * увидел изменение и сохранил его. НЕ трогает mode/lock и НЕ чистит undo-историю/saveStatus.
     *
     * Без этого мост дёргал hydrateCanvas, который немедленно ОЧИЩАЛ свой же dirty → автосейв
     * никогда не видел правок канвы и они не доезжали до сервера (Rule 1 bug пивота).
     */
    reconcileCanvas(state, action: PayloadAction<MindmapSnapshot>) {
      // Снимок ДО правки (по id) — для content-diff dirty (WR-06) и пометки deleted.
      const before = {
        nodes: new Map(state.nodes.map(n => [n.id, n])),
        edges: new Map(state.edges.map(e => [e.id, e]))
      }

      // CR-01/CR-02: Mind-Elixir-снимок несёт синтетические id рёбер (`parent->child`),
      // тогда как слайс/БД хранят рёбра под UUID (createId). Без согласования reconcile
      // удалял бы ВСЕ старые UUID-рёбра и пересоздавал синтетические на каждой правке
      // (churn diff + потеря данных, привязанных к edge.id; reparent = delete+insert
      // вместо смены source). У mindmap-дерева ровно ОДНО входящее ребро на ноду →
      // target уникален. Переиспользуем существующий id ребра по target; createId()
      // только для действительно новых связей. Тогда неизменные рёбра сохраняют UUID,
      // а reparent превращается в смену source того же ребра.
      const prevEdgeByTarget = new Map(state.edges.map(e => [e.target, e.id]))

      state.nodes = action.payload.nodes
      state.edges = action.payload.edges.map(e => ({
        ...e,
        id: prevEdgeByTarget.get(e.target) ?? createId()
      }))

      // WR-04: структурный reconcile НЕ должен сбрасывать выделение. Переносим
      // selectedId по id, если нода ещё существует, иначе снимаем.
      if (state.selectedId && !state.nodes.some(n => n.id === state.selectedId)) {
        state.selectedId = null
      }

      // WR-06: помечаем dirty только реально изменившиеся ноды/рёбра (content-diff),
      // а не весь снимок — иначе автосейв слал бы всю карту на каждую правку.
      reconcileDirtyAfterEdit(state, before)
    },

    /** React Flow контролируемый поток: применить изменения нод + пометить dirty/deleted. */
    onNodesChange(state, action: PayloadAction<NodeChange[]>) {
      state.nodes = applyNodeChanges(action.payload, state.nodes as unknown as RFNode[]) as unknown as MindmapNode[]

      for (const ch of action.payload) {
        if (ch.type === 'remove') {
          state.deleted.nodeIds[ch.id] = true
          delete state.dirty.nodes[ch.id]
        } else if ('id' in ch) {
          state.dirty.nodes[ch.id] = true
        }
      }
    },

    /** React Flow контролируемый поток: применить изменения связей + пометить dirty/deleted. */
    onEdgesChange(state, action: PayloadAction<EdgeChange[]>) {
      state.edges = applyEdgeChanges(action.payload, state.edges as unknown as RFEdge[]) as unknown as RoadmapEdge[]

      for (const ch of action.payload) {
        if (ch.type === 'remove') {
          state.deleted.edgeIds[ch.id] = true
          delete state.dirty.edges[ch.id]
        } else if ('id' in ch) {
          state.dirty.edges[ch.id] = true
        }
      }
    },

    /**
     * Создать корневую ноду на пустой карте (CTA «Добавить корень», MIND-01).
     * Mindmap — однодерево, поэтому имеет смысл только при отсутствии нод; на непустой карте
     * новый верхний узел не создаём (no-op), чтобы не плодить второй корень/несвязный лес.
     */
    addRoot(state) {
      if (state.nodes.length > 0) return
      pushHistory(state)
      attachRoot(state)
    },

    /**
     * Добавить потомка (D-01, MIND-01): Tab на ноде. Создаёт Node(data.kind='mindmap')
     * + Edge parent→child, помечает оба dirty, выделяет новую ноду.
     */
    addChild(state, action: PayloadAction<{ parentId: string }>) {
      pushHistory(state)
      attachChild(state, action.payload.parentId)
    },

    /**
     * Добавить сиблинга (D-01): Enter на ноде. Новая нода получает того же родителя,
     * что у nodeId (через входящий edge). Если nodeId — корень без родителя:
     * mindmap однодерево → создаём ребёнка корня (а не второй корень), чтобы дерево
     * осталось связным.
     */
    addSibling(state, action: PayloadAction<{ nodeId: string }>) {
      const { nodeId } = action.payload
      const edge = incomingEdge(state, nodeId)
      // Родитель сиблинга = родитель nodeId; для корня (нет входящего ребра) → сам корень.
      const parentId = edge ? edge.source : nodeId

      pushHistory(state)
      attachChild(state, parentId)
    },

    /**
     * Переподвешивание (D-05, MIND-02): drag ноды на нового родителя. Меняет fromNodeId
     * (source) входящего ребра ноды на newParentId, помечает ребро dirty.
     * Анти-цикл (T-14-02-01): newParentId не должен быть потомком nodeId и не сам nodeId —
     * иначе дерево станет циклическим (отклоняем, state не меняем).
     */
    reparent(state, action: PayloadAction<{ nodeId: string; newParentId: string }>) {
      const { nodeId, newParentId } = action.payload

      if (nodeId === newParentId) return

      const edge = incomingEdge(state, nodeId)

      if (!edge) return // корень не переподвешиваем
      if (edge.source === newParentId) return // уже у этого родителя — no-op

      // Анти-цикл: новый родитель не должен быть в поддереве перемещаемой ноды.
      if (descendantsOf(state, nodeId).has(newParentId)) return

      pushHistory(state)
      edge.source = newParentId
      state.dirty.edges[edge.id] = true
    },

    /**
     * Базовый цвет ноды/ветки (D-12/D-13, MIND-07). Пишет data.color + data.colorOverride.
     * override=true → цвет задан явно (переопределяет наследование от родителя).
     */
    setColor(state, action: PayloadAction<{ id: string; color: SwatchKey | null; override: boolean }>) {
      const node = state.nodes.find(n => n.id === action.payload.id)

      if (!node) return

      pushHistory(state)
      node.data = { ...node.data, color: action.payload.color, colorOverride: action.payload.override }
      state.dirty.nodes[node.id] = true
    },

    /** Правка data ноды (инлайн-редактор текста, D-02). */
    updateNodeData(state, action: PayloadAction<{ id: string; data: MindmapNodeData }>) {
      const node = state.nodes.find(n => n.id === action.payload.id)

      if (!node) return

      pushHistory(state)
      node.data = action.payload.data
      state.dirty.nodes[node.id] = true
    },

    /**
     * Свёртывание/развёртывание ветки (MIND-03, событие expandNode моста плана 08).
     * D-06 ОТМЕНЁН (пивот 14-08): collapse — часть ДАННЫХ карты (data.expanded), а не
     * личный localStorage. Узкий редьюсер (чище updateNodeData для одного поля): помечает
     * dirty + pushHistory → автосейв/версии подхватывают свёртывание как правку карты.
     */
    setExpanded(state, action: PayloadAction<{ id: string; expanded: boolean }>) {
      const node = state.nodes.find(n => n.id === action.payload.id)

      if (!node) return
      if ((node.data.expanded ?? true) === action.payload.expanded) return // no-op — без лишнего dirty/history

      pushHistory(state)
      node.data = { ...node.data, expanded: action.payload.expanded }
      state.dirty.nodes[node.id] = true
    },

    /** Выделить ноду (или снять выделение). */
    select(state, action: PayloadAction<{ id: string } | null>) {
      state.selectedId = action.payload?.id ?? null
    },

    /**
     * Удалить выделенную ноду (D-01, Delete). Каскадно удаляет инцидентные рёбра
     * (входящее + исходящие). Поддерево остаётся «висеть» на orphan-рёбрах? Нет —
     * onDelete:Cascade на сервере каскадит поддерево; здесь чистим прямые рёбра ноды,
     * сервер довершает каскад при удалении самой ноды.
     */
    deleteSelected(state) {
      const { selectedId } = state

      if (!selectedId) return

      pushHistory(state)

      state.nodes = state.nodes.filter(n => n.id !== selectedId)
      state.deleted.nodeIds[selectedId] = true
      delete state.dirty.nodes[selectedId]

      for (const e of state.edges) {
        if (e.source === selectedId || e.target === selectedId) {
          state.deleted.edgeIds[e.id] = true
          delete state.dirty.edges[e.id]
        }
      }

      state.edges = state.edges.filter(e => e.source !== selectedId && e.target !== selectedId)
      state.selectedId = null
    },

    /** Undo: вытолкнуть past → future, восстановить срез + согласовать dirty (CR-02). */
    undo(state) {
      const prev = state.past.pop()

      if (!prev) return

      const before = { nodes: state.nodes.map(n => n.id), edges: state.edges.map(e => e.id) }

      state.future.push(snapshot(state))
      state.nodes = prev.nodes
      state.edges = prev.edges

      reconcileDirtyAfterRestore(state, before)
    },

    /** Redo: вытолкнуть future → past, восстановить срез + согласовать dirty (CR-02). */
    redo(state) {
      const next = state.future.pop()

      if (!next) return

      const before = { nodes: state.nodes.map(n => n.id), edges: state.edges.map(e => e.id) }

      state.past.push(snapshot(state))
      state.nodes = next.nodes
      state.edges = next.edges

      reconcileDirtyAfterRestore(state, before)
    },

    /** Сброс undo-сессии при выходе из edit-режима. */
    resetEditSession(state) {
      state.past = []
      state.future = []
    },

    /** Индикатор сохранения. */
    setSaveStatus(state, action: PayloadAction<MindmapState['saveStatus']>) {
      state.saveStatus = action.payload
    },

    /** Переключить режим view/edit (LOCK-01). */
    setMode(state, action: PayloadAction<MindmapState['mode']>) {
      state.mode = action.payload
    },

    /** Записать снимок лока (из status-poll / heartbeat-ответа). null = лок свободен. */
    setLock(state, action: PayloadAction<MindmapState['lock']>) {
      state.lock = action.payload
    },

    /** Очистить dirty/deleted после успешного сейва. */
    clearDirty(state) {
      state.dirty = { nodes: {}, edges: {} }
      state.deleted = { nodeIds: {}, edgeIds: {} }
    }
  }
})

export const {
  hydrate,
  hydrateCanvas,
  reconcileCanvas,
  onNodesChange,
  onEdgesChange,
  addRoot,
  addChild,
  addSibling,
  reparent,
  setColor,
  updateNodeData,
  setExpanded,
  select,
  deleteSelected,
  undo,
  redo,
  resetEditSession,
  setSaveStatus,
  setMode,
  setLock,
  clearDirty
} = slice.actions

export default slice.reducer
